import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
    useGetAdminMarathonsQuery,
    useGetAwardableMarathonsQuery,
    usePreviewMarathonPrizeMutation,
    useAwardMarathonPrizeMutation,
} from "../api/marathonAdmin";
import type {
    AdminAwardableMarathon,
    AdminMarathonAwardPreview,
    AdminMarathonFilters,
} from "../types/marathonAdmin";
import { useNotification } from "../hooks/useNotification";
import Notification from "../components/ui/Notification";

const typeOptions = [
    { value: "all", labelKey: "marathon:types.all" },
    { value: "1", labelKey: "marathon:types.type_1" },
    { value: "2", labelKey: "marathon:types.type_2" },
    { value: "3", labelKey: "marathon:types.type_3" },
];

const statusOptions = [
    { value: "all", labelKey: "marathon:statuses.all" },
    { value: "0", labelKey: "marathon:statuses.open" },
    { value: "1", labelKey: "marathon:statuses.active" },
    { value: "2", labelKey: "marathon:statuses.finished" },
];

const awardBlockingReasonLabels: Record<string, string> = {
    already_awarded: "уже начислено",
    participant_not_found: "аккаунт участника не найден",
    marathon_fee_not_paid:
        "марафон за этот цикл не оплачен (fee=0) — начислить нельзя",
};

const snapshotSourceHelp: Record<string, { short: string; full: string }> = {
    system: {
        short: "снимок дня розыгрыша",
        full: "Автоматический снимок билетов в день марафона. Начисление идёт по нему — это «фото» билетов на момент розыгрыша.",
    },
    backup: {
        short: "исторический бэкап",
        full: "Снимок из старого бэкапа (восстановленные данные прошлого розыгрыша).",
    },
    cleanup_guard: {
        short: "снимок перед очисткой",
        full: "Снимок, сохранённый перед очисткой номеров/ставок, чтобы не потерять данные розыгрыша.",
    },
    manual_command: {
        short: "ручной снимок",
        full: "Снимок, созданный вручную командой/админом.",
    },
};

const MarathonsPage: React.FC = () => {
    const { t } = useTranslation(["marathon", "common"]);
    const { showError, showSuccess, notification, hideNotification } =
        useNotification();
    const [filters, setFilters] = useState({
        type: "all",
        status: "all",
        search: "",
        sort_by: "status" as NonNullable<AdminMarathonFilters["sort_by"]>,
        sort_order: "asc" as NonNullable<AdminMarathonFilters["sort_order"]>,
    });
    const [pagination, setPagination] = useState({ page: 1, perPage: 20 });

    // Состояние формы начисления призовых
    const [prizeForm, setPrizeForm] = useState({
        marathonId: "",
        snapshotId: "",
        ticketNumber: "",
    });
    const [awardPreview, setAwardPreview] =
        useState<AdminMarathonAwardPreview | null>(null);
    const [showOnlyNotAwarded, setShowOnlyNotAwarded] = useState(false);
    const [showSnapshotHelp, setShowSnapshotHelp] = useState(false);

    const queryFilters: AdminMarathonFilters = {
        page: pagination.page,
        per_page: pagination.perPage,
        type: filters.type !== "all" ? Number(filters.type) : undefined,
        status: filters.status !== "all" ? Number(filters.status) : undefined,
        search: filters.search.trim() || undefined,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
    };

    const { data, isLoading, isFetching } =
        useGetAdminMarathonsQuery(queryFilters);

    // порожні «черга» марафони (status=open, 0 учасників) не показуємо
    const marathons = useMemo(
        () =>
            (data?.data ?? []).filter(
                (m) => !(m.status === 0 && (m.participants_total ?? 0) === 0)
            ),
        [data?.data]
    );
    const meta = data?.meta;

    const {
        data: awardableMarathonsData,
        isError: isAwardableMarathonsError,
        isLoading: isAwardableMarathonsLoading,
    } = useGetAwardableMarathonsQuery();
    const awardableMarathons = useMemo(
        () => awardableMarathonsData?.data ?? [],
        [awardableMarathonsData?.data]
    );
    const awardableMeta = awardableMarathonsData?.meta;
    const awardCycleDate = awardableMeta?.cycle_date ?? null;

    const awardedCount =
        awardableMeta?.awarded_count ??
        awardableMarathons.filter((m) => m.is_awarded).length;
    const pendingAwardCount =
        awardableMeta?.pending_count ??
        awardableMarathons.length - awardedCount;

    const visibleAwardableMarathons = useMemo(
        () =>
            showOnlyNotAwarded
                ? awardableMarathons.filter((m) => !m.is_awarded)
                : awardableMarathons,
        [awardableMarathons, showOnlyNotAwarded]
    );

    const selectedAwardable = useMemo(
        () =>
            awardableMarathons.find(
                (m) =>
                    String(m.marathon_id) === prizeForm.marathonId &&
                    String(m.snapshot_id ?? "") === prizeForm.snapshotId
            ) ?? null,
        [awardableMarathons, prizeForm.marathonId, prizeForm.snapshotId]
    );

    // Мутация для начисления призовых
    const [previewPrize, { isLoading: isPreviewingPrize }] =
        usePreviewMarathonPrizeMutation();
    const [awardPrize, { isLoading: isAwardingPrize }] =
        useAwardMarathonPrizeMutation();

    const summaryCards = useMemo(() => {
        const totalParticipants = marathons.reduce(
            (sum, marathon) => sum + (marathon.participants_total ?? 0),
            0
        );
        const totalPaid = marathons.reduce(
            (sum, marathon) => sum + (marathon.participants_paid ?? 0),
            0
        );
        const totalQueue = marathons.reduce(
            (sum, marathon) => sum + (marathon.queue_count ?? 0),
            0
        );

        return [
            {
                label: t("marathon:metrics.participants"),
                value: totalParticipants,
            },
            {
                label: t("marathon:metrics.paid"),
                value: totalPaid,
            },
            {
                label: t("marathon:metrics.pending"),
                value: totalQueue,
            },
        ];
    }, [marathons, t]);

    const handleFilterChange = (
        key: keyof typeof filters,
        value: string
    ): void => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, page: 1 }));
    };

    const handleResetFilters = () => {
        setFilters({
            type: "all",
            status: "all",
            search: "",
            sort_by: "status",
            sort_order: "asc",
        });
        setPagination({ page: 1, perPage: 20 });
    };

    const handlePageChange = (direction: "prev" | "next") => {
        if (!meta) {
            return;
        }

        if (direction === "prev" && pagination.page > 1) {
            setPagination((prev) => ({ ...prev, page: prev.page - 1 }));
        }

        if (
            direction === "next" &&
            pagination.page < (meta?.last_page ?? pagination.page)
        ) {
            setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
        }
    };

    const buildAwardPayload = () => ({
        marafone_id: Number(prizeForm.marathonId),
        ticket_number: Number(prizeForm.ticketNumber),
        snapshot_id: prizeForm.snapshotId ? Number(prizeForm.snapshotId) : null,
    });

    const handlePreviewPrize = async () => {
        if (!prizeForm.marathonId || !prizeForm.ticketNumber) {
            showError("Выберите марафон и номер билета");
            return;
        }

        try {
            const result = await previewPrize(buildAwardPayload()).unwrap();
            setAwardPreview(result.data);
        } catch (error: any) {
            setAwardPreview(null);
            const errorMessage =
                error?.data?.message || error?.message || "Ошибка при проверке победителя";
            showError(errorMessage);
        }
    };

    const handleAwardPrize = async () => {
        if (!prizeForm.marathonId || !prizeForm.ticketNumber) {
            showError("Заполните все поля");
            return;
        }

        try {
            const result = await awardPrize(buildAwardPayload()).unwrap();

            if (result.success) {
                showSuccess(result.message || "Призовые успешно начислены");
                setPrizeForm({
                    marathonId: "",
                    snapshotId: "",
                    ticketNumber: "",
                });
                setAwardPreview(null);
            }
        } catch (error: any) {
            const errorMessage =
                error?.data?.message || error?.message || "Ошибка при начислении призовых";
            showError(errorMessage);
        }
    };

    const selectAwardableMarathon = (marathon: AdminAwardableMarathon) => {
        setPrizeForm((prev) => ({
            ...prev,
            marathonId: String(marathon.marathon_id),
            snapshotId: marathon.snapshot_id ? String(marathon.snapshot_id) : "",
        }));
        setAwardPreview(null);
    };

    const awardableKey = (marathon: AdminAwardableMarathon) =>
        `${marathon.marathon_id}:${marathon.snapshot_id ?? "live"}`;

    return (
        <>
            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />
            <div className="px-4 py-6 space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                    {t("marathon:admin_title")}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                    {t("marathon:admin_subtitle")}
                </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {summaryCards.map((card) => (
                    <div
                        key={card.label}
                        className="bg-white dark:bg-gray-800 rounded-lg shadow p-4"
                    >
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {card.label}
                        </p>
                        <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                            {card.value}
                        </p>
                    </div>
                ))}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t("marathon:filters.type")}
                        </label>
                        <select
                            value={filters.type}
                            onChange={(e) =>
                                handleFilterChange("type", e.target.value)
                            }
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        >
                            {typeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {t(option.labelKey, { ns: "marathon" })}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t("marathon:filters.status")}
                        </label>
                        <select
                            value={filters.status}
                            onChange={(e) =>
                                handleFilterChange("status", e.target.value)
                            }
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        >
                            {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {t(option.labelKey, { ns: "marathon" })}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Сортировка
                        </label>
                        <select
                            value={filters.sort_by}
                            onChange={(e) =>
                                handleFilterChange("sort_by", e.target.value)
                            }
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        >
                            <option value="status">По статусу</option>
                            <option value="sum">По сумме (100/50/10)</option>
                            <option value="time">По дате старта</option>
                            <option value="id">По ID</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Порядок
                        </label>
                        <select
                            value={filters.sort_order}
                            onChange={(e) =>
                                handleFilterChange("sort_order", e.target.value)
                            }
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        >
                            <option value="asc">По возрастанию</option>
                            <option value="desc">По убыванию</option>
                        </select>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t("marathon:filters.search")}
                        </label>
                        <input
                            type="text"
                            value={filters.search}
                            onChange={(e) =>
                                handleFilterChange("search", e.target.value)
                            }
                            placeholder={t("common:search")}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                        />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={handleResetFilters}
                        className="px-4 py-2 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        {t("marathon:filters.reset")}
                    </button>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
                {isLoading ? (
                    <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                        {t("common:loading")}
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.id")}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.type")}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.status")}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.participants")}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.tickets")}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.bets")}
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {t("marathon:table_columns.actions")}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {marathons.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={7}
                                        className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                                    >
                                        {t("marathon:no_data")}
                                    </td>
                                </tr>
                            ) : (
                                marathons.map((marathon) => (
                                    <tr key={marathon.id}>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {marathon.id}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {marathon.type_label}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                                {marathon.status_label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {marathon.participants_paid}/
                                            {marathon.participants_total}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {marathon.tickets_active}/
                                            {marathon.tickets_total}
                                        </td>
                                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                                            {marathon.bets_total}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <Link
                                                to={`/dashboard/marathons/${marathon.id}`}
                                                className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                                            >
                                                {t("marathon:view_details")}
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}

                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                        {meta &&
                            t("marathon:pagination.page", {
                                page: meta.current_page,
                                total: meta.last_page,
                            })}
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => handlePageChange("prev")}
                            disabled={pagination.page <= 1 || isFetching}
                            className="px-3 py-1 text-sm border rounded disabled:opacity-50 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                        >
                            {t("common:previous")}
                        </button>
                        <button
                            type="button"
                            onClick={() => handlePageChange("next")}
                            disabled={
                                !meta ||
                                pagination.page >= meta.last_page ||
                                isFetching
                            }
                            className="px-3 py-1 text-sm border rounded disabled:opacity-50 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
                        >
                            {t("common:next")}
                        </button>
                    </div>
                </div>
            </div>

            {/* Форма начисления призовых */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-200">
                            Начислить призовые победителям
                        </h2>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            {awardCycleDate
                                ? `Цикл розыгрыша ${awardCycleDate}: кликните карточку → билет → проверить → начислить.`
                                : "Карточки появляются в день марафона, когда создаются снимки розыгрыша."}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                            <span aria-hidden>✓</span>
                            Начислено: {awardedCount}
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                            <span aria-hidden>○</span>
                            Ждут: {pendingAwardCount}
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                            Всего: {awardableMarathons.length}
                        </span>
                    </div>
                </div>

                <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50/80 dark:border-blue-900 dark:bg-blue-950/40 p-3 text-sm text-blue-900 dark:text-blue-100">
                    <button
                        type="button"
                        onClick={() => setShowSnapshotHelp((v) => !v)}
                        className="font-medium underline-offset-2 hover:underline"
                    >
                        {showSnapshotHelp ? "Скрыть пояснение" : "Что такое «снимок» и галочки?"}
                    </button>
                    {showSnapshotHelp && (
                        <ul className="mt-2 list-disc pl-5 space-y-1 text-blue-900/90 dark:text-blue-100/90">
                            <li>
                                <strong>Снимок</strong> — сохранённый список билетов на день розыгрыша.
                                Начисляем по снимку, а не по «живым» билетам (их уже могли поменять).
                            </li>
                            <li>
                                <strong>Снимок дня розыгрыша</strong> (раньше «текущий снимок») —
                                обычный автоматический снимок в день марафона.
                            </li>
                            <li>
                                <strong>Галочка ✓</strong> — призовые уже начислены.
                                Пустой кружок ○ — ещё нужно начислить.
                            </li>
                        </ul>
                    )}
                </div>

                <div className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Марафоны текущего цикла
                            {awardCycleDate ? ` (${awardCycleDate})` : ""}
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={showOnlyNotAwarded}
                                onChange={(e) => setShowOnlyNotAwarded(e.target.checked)}
                                className="rounded border-gray-300"
                            />
                            Показать только неначисленные
                        </label>
                    </div>

                    {isAwardableMarathonsLoading && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Загрузка марафонов...
                        </p>
                    )}
                    {isAwardableMarathonsError && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                            Не удалось загрузить марафоны. Проверьте права администратора, API key и IP whitelist.
                        </p>
                    )}
                    {!isAwardableMarathonsLoading &&
                        !isAwardableMarathonsError &&
                        awardableMarathons.length === 0 && (
                            <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-6 text-sm text-gray-600 dark:text-gray-300 space-y-2">
                                <p className="font-medium text-gray-900 dark:text-gray-100">
                                    Сейчас пусто — начислять нечего
                                </p>
                                <p>
                                    Карточки появляются автоматически в день розыгрыша, когда система
                                    делает снимки билетов. Пока снимков нет — кнопки «Проверить /
                                    Начислить» будут неактивны (нужно сначала выбрать карточку).
                                </p>
                            </div>
                        )}
                    {!isAwardableMarathonsLoading &&
                        !isAwardableMarathonsError &&
                        awardableMarathons.length > 0 &&
                        visibleAwardableMarathons.length === 0 &&
                        showOnlyNotAwarded && (
                            <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30 p-4 text-sm text-green-800 dark:text-green-200">
                                Все марафоны этого цикла уже с галочкой ✓ Начислено.
                                Снимите фильтр «только неначисленные», чтобы увидеть их.
                            </div>
                        )}

                    {visibleAwardableMarathons.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Показано {visibleAwardableMarathons.length}
                            {showOnlyNotAwarded
                                ? ` из ${awardableMarathons.length}`
                                : ""}
                            {visibleAwardableMarathons.length > 4
                                ? " — список прокручивается, если карточек много"
                                : ""}
                            . Жёлтый ○ = ещё нужно начислить, зелёный ✓ = уже готово.
                        </p>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[32rem] overflow-y-auto pr-1">
                        {visibleAwardableMarathons.map((marathon) => {
                            const key = awardableKey(marathon);
                            const selected =
                                String(marathon.marathon_id) === prizeForm.marathonId &&
                                String(marathon.snapshot_id ?? "") === prizeForm.snapshotId;
                            const isAwarded = Boolean(marathon.is_awarded);
                            const sourceHelp =
                                snapshotSourceHelp[marathon.snapshot_source ?? ""] ??
                                (marathon.has_snapshot
                                    ? {
                                          short: "снимок",
                                          full: "Сохранённый снимок билетов розыгрыша.",
                                      }
                                    : {
                                          short: "без снимка (live)",
                                          full: "Снимка нет — работаем по текущим живым билетам марафона.",
                                      });

                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => selectAwardableMarathon(marathon)}
                                    className={`text-left rounded-lg border p-4 transition ${
                                        selected
                                            ? "border-blue-500 ring-2 ring-blue-200 dark:ring-blue-900 bg-blue-50/50 dark:bg-blue-950/30"
                                            : "border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700"
                                    } ${isAwarded ? "opacity-90" : ""}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <span
                                            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded border text-sm font-bold ${
                                                isAwarded
                                                    ? "border-green-600 bg-green-600 text-white"
                                                    : "border-amber-500 text-amber-600 bg-white dark:bg-gray-900"
                                            }`}
                                            title={isAwarded ? "Уже начислено" : "Ещё не начислено"}
                                            aria-label={isAwarded ? "Начислено" : "Не начислено"}
                                        >
                                            {isAwarded ? "✓" : ""}
                                        </span>
                                        <div className="min-w-0 flex-1 space-y-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="font-semibold text-gray-900 dark:text-gray-100">
                                                    {marathon.type_label}
                                                </span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    ID марафона {marathon.marathon_id}
                                                    {marathon.snapshot_id
                                                        ? ` · снимок #${marathon.snapshot_id}`
                                                        : ""}
                                                </span>
                                            </div>
                                            <div className="text-sm text-gray-700 dark:text-gray-300">
                                                Дата розыгрыша:{" "}
                                                <strong>
                                                    {marathon.draw_date ?? "— (live)"}
                                                </strong>
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {sourceHelp.short}
                                                {" · "}
                                                билетов с номерами: {marathon.draw_numbers_count}
                                            </div>
                                            <div
                                                className={`text-sm font-medium ${
                                                    isAwarded
                                                        ? "text-green-700 dark:text-green-400"
                                                        : "text-amber-700 dark:text-amber-400"
                                                }`}
                                            >
                                                {isAwarded
                                                    ? `✓ Начислено${
                                                          marathon.winner_user
                                                              ? ` — ${marathon.winner_user.name ?? ""} (ID ${marathon.winner_user.id})${
                                                                    marathon.winning_draw_number
                                                                        ? `, билет ${marathon.winning_draw_number}`
                                                                        : ""
                                                                }`
                                                              : ""
                                                      }`
                                                    : "○ Ещё не начислено — можно выбрать и выплатить"}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {selectedAwardable && (
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3 bg-gray-50 dark:bg-gray-900/40">
                            <div className="text-sm text-gray-700 dark:text-gray-200">
                                <strong>Выбрано:</strong> {selectedAwardable.type_label}, марафон #
                                {selectedAwardable.marathon_id}
                                {selectedAwardable.draw_date
                                    ? `, розыгрыш ${selectedAwardable.draw_date}`
                                    : ""}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {(
                                    snapshotSourceHelp[
                                        selectedAwardable.snapshot_source ?? ""
                                    ] ?? {
                                        full: selectedAwardable.has_snapshot
                                            ? "Сохранённый снимок билетов розыгрыша."
                                            : "Снимка нет — работаем по текущим живым билетам.",
                                    }
                                ).full}
                            </p>
                            {selectedAwardable.is_awarded ? (
                                <p className="text-sm text-green-700 dark:text-green-400">
                                    Призовые по этому снимку уже начислены. Повторно начислять нельзя.
                                </p>
                            ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Номер билета-победителя
                                    </label>
                                    <input
                                        type="number"
                                        value={prizeForm.ticketNumber}
                                        onChange={(e) => {
                                            setPrizeForm((prev) => ({
                                                ...prev,
                                                ticketNumber: e.target.value,
                                            }));
                                            setAwardPreview(null);
                                        }}
                                        placeholder="Например 24"
                                        className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {awardPreview && (
                        <div
                            className={`rounded border p-4 text-sm ${
                                awardPreview.can_award
                                    ? "border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-950/40 dark:text-green-100"
                                    : "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
                            }`}
                        >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <span className="block opacity-70">Победитель</span>
                                    <span>
                                        ID: {awardPreview.winner_user.id}
                                        {awardPreview.winner_user.name
                                            ? ` - ${awardPreview.winner_user.name}`
                                            : ""}
                                    </span>
                                </div>
                                <div>
                                    <span className="block opacity-70">Билет</span>
                                    <span>
                                        #{awardPreview.ticket_id}, номер{" "}
                                        {awardPreview.winning_draw_number}
                                    </span>
                                </div>
                                <div>
                                    <span className="block opacity-70">Статус</span>
                                    <span>
                                        {awardPreview.can_award
                                            ? "можно начислять"
                                            : awardPreview.award_status ?? "нельзя начислить"}
                                        {awardPreview.blocking_reason
                                            ? ` (${
                                                  awardBlockingReasonLabels[
                                                      awardPreview.blocking_reason
                                                  ] ?? awardPreview.blocking_reason
                                              })`
                                            : ""}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={handlePreviewPrize}
                            disabled={
                                isPreviewingPrize ||
                                !prizeForm.marathonId ||
                                !prizeForm.ticketNumber ||
                                Boolean(selectedAwardable?.is_awarded)
                            }
                            className="px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed dark:hover:bg-blue-950/40"
                        >
                            {isPreviewingPrize ? "Проверка..." : "Проверить победителя"}
                        </button>
                        <button
                            type="button"
                            onClick={handleAwardPrize}
                            disabled={
                                isAwardingPrize ||
                                !prizeForm.marathonId ||
                                !prizeForm.ticketNumber ||
                                !awardPreview ||
                                !awardPreview.can_award ||
                                Boolean(selectedAwardable?.is_awarded)
                            }
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isAwardingPrize ? "Начисление..." : "Начислить призовые"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default MarathonsPage;
